import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
// Use generated submit endpoint via our enhanced service
import { Tooltip } from '@/components/ui/v3/Tooltip';
import {
  Z_INDEX_DROPDOWN_IN_MODAL,
  Z_INDEX_MODAL_NESTED,
} from '@/components/ui/zIndex';
import { useMatter } from '@/hooks/useMatter';
import { useRequestFaxStatus } from '@/hooks/useRequestFaxStatus';
import { firmsApiClient } from '@/services/api/firmsService';
import type {
  SubmitHipaaPackageSchema,
  TreatmentRecordRequestSchema,
} from '@/services/api/generated/treatmentRecordRequestsService';
import { hipaaPackageApi } from '@/services/api/hipaaPackageService';
import { mattersApiClient } from '@/services/api/mattersService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import { MedicalProvider } from '@/services/types/treatment-types';

import {
  BlockedState,
  ResolveBlockersButton,
} from '../tabs/records/ResolveBlockersButton';
import {
  BaseRequestStepProps,
  PizzaTrackerStep,
  useUpdateRequestStatus,
} from './RequestStatusTrackerStep';

type PizzaTrackerStepState = 'complete' | 'up_next' | 'not_started';

type ChannelSubmitStepProps = BaseRequestStepProps;

type FirmFrontChannel = {
  id?: string | null;
  address?: string | null;
};

type EmailSenderOption = 'firm' | 'records';

const RECORDS_SENDER_ADDRESS = 'records@finchlegal.com';
// Front channel ID for records@finchlegal.com
const RECORDS_FRONT_CHANNEL_ID = 'cha_j9s4e';

type SubmitHipaaPackageSchemaWithFrontChannelOverride = SubmitHipaaPackageSchema & {
  front_channel_id?: string;
};

const TrackerContent = ({
  request,
  provider,
  state,
  actionText,
  handleAction,
  isActionInProgress,
  faxFailed,
  handleManualOverride,
  isActionDisabled,
  disabledReason,
}: {
  request: TreatmentRecordRequestSchema;
  provider: MedicalProvider;
  state: PizzaTrackerStepState;
  actionText: string;
  handleAction: () => void;
  isActionInProgress: boolean;
  faxFailed?: boolean;
  handleManualOverride?: () => void;
  isActionDisabled?: boolean;
  disabledReason?: string | null;
}) => {
  const isAutomated =
    request.actor === 'agent' &&
    request.metadata?.automation_state_v2?.status !== 'idle';

  const hasBlockers =
    request.metadata?.automation_state_v2?.status === 'blocked' &&
    request.metadata?.automation_state_v2?.blocked_by?.length &&
    request.metadata?.automation_state_v2?.blocked_by?.length > 0 &&
    state === 'up_next';

  const isUpNext = state === 'up_next' || state === 'not_started';

  const handleOverride = () => {
    handleAction();
  };

  if (isAutomated && hasBlockers) {
    return (
      <div className="flex gap-0">
        <ResolveBlockersButton
          request={request}
          blockedState={request.metadata?.automation_state_v2 as BlockedState}
          provider={provider}
          onOverride={handleOverride}
          isDisabled={isActionInProgress}
          highZIndex
        />
      </div>
    );
  }
  if (isUpNext) {
    return (
      <div className="flex flex-col gap-2">
        {isActionDisabled && disabledReason ? (
          <Tooltip content={disabledReason} side="top">
            <div className="w-full">
              <Button
                color="indigo-steel"
                onClick={handleAction}
                disabled={isActionInProgress || !!isActionDisabled}
                className="w-full"
              >
                {actionText}
              </Button>
            </div>
          </Tooltip>
        ) : (
          <div className="w-full">
            <Button
              color="indigo-steel"
              onClick={handleAction}
              disabled={isActionInProgress || !!isActionDisabled}
              className="w-full"
            >
              {actionText}
            </Button>
          </div>
        )}
        {faxFailed && handleManualOverride && (
          <Button
            color="zinc"
            onClick={handleManualOverride}
            disabled={isActionInProgress}
            className="text-xs"
          >
            Continue without fax confirmation
          </Button>
        )}
      </div>
    );
  }

  return null;
};

export const ChannelSubmitStep = (props: ChannelSubmitStepProps) => {
  const { request, provider, nextStatus, state, status, title } = props;
  const { matterId } = useMatter();

  // Fetch matter data to get firm ID
  const { data: matterData } = mattersApiClient.useGetMatterQuery(matterId);
  const firmId = matterData?.firm?.id;

  // Fetch full firm data to access Front channel
  const { data: firmData } = firmsApiClient.useGetFirmQuery(firmId || '', {
    skip: !firmId,
  });
  const firmFrontChannel = firmData?.front_channel as FirmFrontChannel | null | undefined;

  const [isActionInProgress, setIsActionInProgress] = useState(false);
  const [hasFaxBeenInitiated, setHasFaxBeenInitiated] = useState(false);
  const [faxFailed, setFaxFailed] = useState(false);
  const [statusProcessed, setStatusProcessed] = useState(false);
  const [hipaaPackageSubmitted, setHipaaPackageSubmitted] = useState(false);
  const includeChequeId = useId();
  const emailToId = useId();
  const emailSubjectId = useId();
  const emailBodyId = useId();
  // Mail cheque UI
  const [includeCheque, setIncludeCheque] = useState(false);
  const [chequeAmount, setChequeAmount] = useState('');
  const [chequeMemo, setChequeMemo] = useState('');
  const [mailError, setMailError] = useState<string | null>(null);
  const [showMailSubmitModal, setShowMailSubmitModal] = useState(false);
  // Mail options
  const [mailingClass, setMailingClass] = useState<string>('usps_first_class');
  const [includeReturnPostage, setIncludeReturnPostage] =
    useState<boolean>(true);
  const returnPostageId = useId();
  // Fax cover page UI
  const [showFaxSubmitModal, setShowFaxSubmitModal] = useState(false);
  const [faxCoverTo, setFaxCoverTo] = useState('');
  const [faxCoverFrom, setFaxCoverFrom] = useState('');
  const [faxCoverMessage, setFaxCoverMessage] = useState('');
  // Email submission UI
  const [showEmailSubmitModal, setShowEmailSubmitModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSenderOption, setEmailSenderOption] =
    useState<EmailSenderOption>('firm');
  const [emailValidationError, setEmailValidationError] = useState<
    string | null
  >(null);
  const [emailTemplateInitialized, setEmailTemplateInitialized] =
    useState(false);
  const MAIL_SUBMIT_WAIT_MS = 120000; // 2 min grace window for Finchy/blockers to surface
  const waitKey = `mail_submit_wait_until_${matterId}_${provider.id}_${request.id}`;
  const [mailWaitUntil, setMailWaitUntil] = useState<number | null>(null);
  const isInMailWaitWindow = useMemo(
    () => !!mailWaitUntil && Date.now() < mailWaitUntil,
    [mailWaitUntil],
  );
  const [autoAdvanceTimer, setAutoAdvanceTimer] = useState<number | null>(null);
  const isSchedulingRef = useRef(false);
  // Keep latest automation status in a ref to avoid stale closures in timers
  const automationStatusRef = useRef(
    request.metadata?.automation_state_v2?.status,
  );
  useEffect(() => {
    automationStatusRef.current = request.metadata?.automation_state_v2?.status;
  }, [request.metadata?.automation_state_v2?.status]);

  // Track latest blockers state in a ref for timer usage
  const hasBlockersRef = useRef<boolean>(
    (() => {
      const auto = request.metadata?.automation_state_v2;
      const isBlocked = auto?.status === 'blocked';
      const blockedByLen = isBlocked
        ? ((auto as any)?.blocked_by?.length ?? 0)
        : 0;
      return !!(isBlocked && blockedByLen > 0);
    })(),
  );
  useEffect(() => {
    const auto = request.metadata?.automation_state_v2;
    const isBlocked = auto?.status === 'blocked';
    const blockedByLen = isBlocked
      ? ((auto as any)?.blocked_by?.length ?? 0)
      : 0;
    hasBlockersRef.current = !!(isBlocked && blockedByLen > 0);
  }, [request.metadata?.automation_state_v2]);
  const MAX_CHEQUE_AMOUNT = 250; // UI cap; backend allows higher, but we enforce product policy here

  const [submitHipaaPackage] =
    treatmentRecordRequestService.useSubmitHipaaPackageMutation();

  const { changeStatus } = useUpdateRequestStatus({
    requestId: request.id,
    providerId: provider.id,
  });

  const isFaxChannel = request.channel?.type === 'fax';
  const isMailChannel = request.channel?.type === 'mail';
  const isEmailChannel = request.channel?.type === 'email';
  const actionText = isFaxChannel
    ? 'Submit Fax'
    : isMailChannel
      ? 'Submit Mail'
      : isEmailChannel
        ? 'Send Email'
        : 'Mark Submitted';
  const isFaxMissing = useMemo(
    () =>
      isFaxChannel &&
      !(request.channel?.fax && request.channel.fax.trim().length > 0),
    [isFaxChannel, request.channel?.fax],
  );
  const isAddressMissing = useMemo(
    () =>
      isMailChannel &&
      !(request.channel?.address && request.channel.address.trim().length > 0),
    [isMailChannel, request.channel?.address],
  );
  const isEmailMissing = useMemo(
    () =>
      isEmailChannel &&
      !(request.channel?.email && request.channel.email.trim().length > 0),
    [isEmailChannel, request.channel?.email],
  );
  const isFrontChannelMissing = useMemo(
    () =>
      isEmailChannel &&
      !firmFrontChannel?.address &&
      !RECORDS_FRONT_CHANNEL_ID.trim(),
    [isEmailChannel, firmFrontChannel?.address],
  );
  const isFirmChannelMissing = isEmailChannel && !firmFrontChannel?.address;

  // Check HIPAA package availability for gating
  const { data: hipaaPackage } =
    hipaaPackageApi.useRequestHipaaPackageRetrieveQuery(
      {
        matterId,
        providerId: provider.id,
        requestId: request.id,
      },
      { skip: !isMailChannel && !isFaxChannel && !isEmailChannel },
    );
  const isHipaaPackageMissing = useMemo(
    () => !(hipaaPackage && hipaaPackage.completed_package_id),
    [hipaaPackage],
  );

  const emailTemplateQueryArgs = useMemo(
    () => ({
      matterId,
      providerId: provider.id,
      requestId: request.id,
    }),
    [matterId, provider.id, request.id],
  );

  const {
    data: emailTemplate,
    isFetching: isEmailTemplateFetching,
    isLoading: isEmailTemplateLoading,
    isError: isEmailTemplateError,
    error: emailTemplateError,
  } = treatmentRecordRequestService.useGetHipaaEmailTemplateQuery(
    emailTemplateQueryArgs,
    {
      skip: !showEmailSubmitModal,
    },
  );

  // Initialize mail wait window from sessionStorage (persist across refresh in this tab)
  useEffect(() => {
    if (!isMailChannel) return;
    const stored = sessionStorage.getItem(waitKey);
    if (stored) {
      const ts = parseInt(stored, 10);
      if (!Number.isNaN(ts) && Date.now() < ts) {
        setMailWaitUntil(ts);
      } else if (!Number.isNaN(ts)) {
        // Expired, clean up
        sessionStorage.removeItem(waitKey);
      }
    }
  }, [isMailChannel, waitKey]);

  useEffect(() => {
    if (!showEmailSubmitModal) {
      setEmailSubject('');
      setEmailBody('');
      setEmailValidationError(null);
      setEmailTemplateInitialized(false);
      return;
    }

    if (emailTemplate && !emailTemplateInitialized) {
      setEmailSubject(emailTemplate.subject);
      setEmailBody(emailTemplate.body);
      setEmailTemplateInitialized(true);
      setEmailValidationError(null);
    } else if (isEmailTemplateError && !emailTemplateInitialized) {
      setEmailTemplateInitialized(true);
    }
  }, [
    emailTemplate,
    emailTemplateInitialized,
    isEmailTemplateError,
    showEmailSubmitModal,
  ]);

  const emailSenderInitializedRef = useRef(false);
  useEffect(() => {
    if (!showEmailSubmitModal) {
      emailSenderInitializedRef.current = false;
      setEmailSenderOption('firm');
      return;
    }

    if (emailSenderInitializedRef.current) return;
    emailSenderInitializedRef.current = true;

    if (!firmFrontChannel?.address && RECORDS_FRONT_CHANNEL_ID.trim()) {
      setEmailSenderOption('records');
      return;
    }

    setEmailSenderOption('firm');
  }, [firmFrontChannel?.address, showEmailSubmitModal]);

  // Keep a lightweight ref of automation status for guard checks at auto-advance time
  // When props.request updates from parent, this component re-renders
  // and we update the ref value implicitly by capturing latest request.metadata in closures.

  // Schedule auto-advance when in wait window
  const scheduleAutoAdvance = (until: number): number | null => {
    const remaining = until - Date.now();
    if (remaining <= 0) return null;
    const id = window.setTimeout(async () => {
      try {
        // Guard: if automation is blocked/failed, do not auto-advance
        const currentAutomation = automationStatusRef.current;
        if (currentAutomation === 'blocked' || currentAutomation === 'failed') {
          sessionStorage.removeItem(waitKey);
          setMailWaitUntil(null);
          return;
        }
        // Only advance if still in a pre-submission status
        await changeStatus(nextStatus, undefined, hasBlockersRef.current);
      } finally {
        sessionStorage.removeItem(waitKey);
        setMailWaitUntil(null);
        setAutoAdvanceTimer(null);
      }
    }, remaining);
    return id;
  };

  // When we enter a wait window, schedule auto-advance
  useEffect(() => {
    if (
      isMailChannel &&
      mailWaitUntil &&
      Date.now() < mailWaitUntil &&
      !autoAdvanceTimer &&
      !isSchedulingRef.current
    ) {
      isSchedulingRef.current = true;
      const id = scheduleAutoAdvance(mailWaitUntil);
      if (id !== null) {
        setAutoAdvanceTimer(id);
      }
      isSchedulingRef.current = false;
    }
  }, [isMailChannel, mailWaitUntil, autoAdvanceTimer]);

  // Cleanup timer when timer id changes or on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimer) {
        clearTimeout(autoAdvanceTimer);
      }
    };
  }, [autoAdvanceTimer]);

  // Use fax status polling hook
  const { faxStatus, isPolling, startPolling } = useRequestFaxStatus({
    matterId,
    providerId: provider.id,
    requestId: request.id,
    enabled: isFaxChannel && hasFaxBeenInitiated,
    onStatusChange: (faxTransmissionStatus) => {
      if (
        (faxTransmissionStatus?.status === 'sent' ||
          faxTransmissionStatus?.status === 'delivered') &&
        !statusProcessed
      ) {
        // Fax confirmed sent/delivered, now safe to change status
        // Only process this once to prevent race conditions
        setFaxFailed(false);
        handleStatusChangeAfterFaxSent();
      } else if (
        faxTransmissionStatus?.status === 'failed' &&
        !statusProcessed
      ) {
        // Fax failed
        setIsActionInProgress(false);
        setFaxFailed(true);
        setStatusProcessed(true); // Mark as processed to prevent duplicate handling
        toast.error(
          `Fax transmission failed: ${faxTransmissionStatus.error_message || 'Unknown error'}`,
        );
      }
    },
  });

  const hasBlockers = !!(
    request.metadata?.automation_state_v2?.status === 'blocked' &&
    request.metadata?.automation_state_v2?.blocked_by?.length &&
    request.metadata?.automation_state_v2?.blocked_by?.length > 0
  );

  // Handle status change after fax is sent
  const handleStatusChangeAfterFaxSent = async () => {
    // Prevent duplicate processing
    if (statusProcessed) return;

    try {
      setStatusProcessed(true); // Mark as processed early to prevent race conditions
      await changeStatus(nextStatus, undefined, hasBlockers);
      toast.success('Fax sent successfully!');
    } catch (error) {
      toast.error('Failed to update status after fax confirmation');
      setStatusProcessed(false); // Reset on error so user can retry
    } finally {
      setIsActionInProgress(false);
    }
  };

  // Reset and retry fax submission
  const handleRetryFax = async () => {
    setFaxFailed(false);
    setStatusProcessed(false); // Reset status processing flag for retry
    setIsActionInProgress(true);

    try {
      if (hipaaPackageSubmitted) {
        // HIPAA package was already submitted successfully, just retry transmission
        toast.info('Retrying fax transmission...');
        startPolling();
      } else {
        // HIPAA package wasn't submitted or failed, do full retry
        setHasFaxBeenInitiated(false);
        setIsActionInProgress(false); // Reset before calling handleAction
        handleAction();
        // handleAction will manage the loading state
      }
    } catch (error) {
      toast.error('Failed to retry fax transmission');
      setFaxFailed(true);
    } finally {
      // Only reset if we were just retrying transmission (not full action)
      if (hipaaPackageSubmitted) {
        setIsActionInProgress(false);
      }
    }
  };

  // Allow manual override to proceed without fax confirmation
  const handleManualOverride = async () => {
    setIsActionInProgress(true);
    try {
      await changeStatus(nextStatus, undefined, hasBlockers);
      toast.success('Status updated manually');
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setIsActionInProgress(false);
    }
  };

  const handleAction = async () => {
    setIsActionInProgress(true);
    try {
      if (isFaxChannel) {
        if (isFaxMissing) {
          toast.info('Please add a fax number before submitting.');
          setIsActionInProgress(false);
          return;
        }
        if (isHipaaPackageMissing) {
          toast.error('No completed HIPAA package found for request');
          setIsActionInProgress(false);
          return;
        }
        // For fax channels, open a modal to capture optional cover page fields
        setShowFaxSubmitModal(true);
        setIsActionInProgress(false);
        return; // actual submit occurs on confirm from modal
      }

      if (isEmailChannel) {
        if (isEmailMissing) {
          toast.info('Please add an email address before submitting.');
          setIsActionInProgress(false);
          return;
        }
        if (isFrontChannelMissing) {
          toast.error(
            'No email sender channel available. Please configure a firm Front channel or records channel.',
          );
          setIsActionInProgress(false);
          return;
        }
        if (isHipaaPackageMissing) {
          toast.error('No completed HIPAA package found for request');
          setIsActionInProgress(false);
          return;
        }
        setShowEmailSubmitModal(true);
        setIsActionInProgress(false);
        return;
      }

      // Mail channel: open modal first; actual submit happens on confirm
      if (isMailChannel) {
        if (isAddressMissing) {
          toast.info('Please add a mailing address before submitting.');
          setIsActionInProgress(false);
          return;
        }
        if (isHipaaPackageMissing) {
          toast.error('No completed HIPAA package found for request');
          setIsActionInProgress(false);
          return;
        }
        // Open modal and let user confirm
        setShowMailSubmitModal(true);
        setIsActionInProgress(false);
        return;
      }

      // For non-fax channels, change status immediately
      await changeStatus(nextStatus, undefined, hasBlockers);
      // For successful non-fax operations, reset loading state
      setIsActionInProgress(false);
    } catch (error: unknown) {
      const errorStatus = (error as { status?: number })?.status;

      if (isFaxChannel) {
        // Handle specific fax submission errors
        if (errorStatus === 400) {
          toast.error('No completed HIPAA package found for request');
        } else if (errorStatus === 500) {
          toast.error('Failed to trigger HIPAA submission workflow');
        } else {
          toast.error('Failed to submit fax. Please try again.');
        }
      } else if (isMailChannel) {
        // Show server-provided details near cheque controls when possible
        const data = (error as any)?.data;
        const detail: string | undefined =
          data?.detail || data?.error || data?.message;
        setMailError(
          detail || 'Failed to submit mail. Please review cheque details.',
        );
        toast.error('Failed to submit mail');
      } else if (isEmailChannel) {
        toast.error('Failed to prepare email submission');
      } else {
        toast.error('Failed to update status');
      }

      // Reset action progress immediately on any error
      setIsActionInProgress(false);
    } finally {
      // For successful fax submissions, isActionInProgress will be reset
      // when fax completes/fails in the onStatusChange callback or handleStatusChangeAfterFaxSent
      // For non-fax channels and errors, it's already been reset above
    }
  };

  const emailTemplateErrorMessage = useMemo(() => {
    if (!emailTemplateError) return null;
    if ('status' in (emailTemplateError as { status?: number })) {
      const { data } = emailTemplateError as {
        data?: { detail?: string; message?: string; error?: string };
      };
      return (
        data?.detail ||
        data?.message ||
        data?.error ||
        'Unable to load email template. You can still edit the message manually.'
      );
    }
    if ('message' in (emailTemplateError as { message?: string })) {
      return (
        (emailTemplateError as { message?: string }).message ||
        'Unable to load email template. You can still edit the message manually.'
      );
    }
    return 'Unable to load email template. You can still edit the message manually.';
  }, [emailTemplateError]);
  const isEmailTemplatePending =
    !emailTemplateInitialized &&
    (isEmailTemplateLoading || isEmailTemplateFetching);

  // Confirm submission from the Fax modal
  const handleConfirmFaxSubmit = async () => {
    setIsActionInProgress(true);
    try {
      // Build payload with optional cover page fields if provided
      const payload: SubmitHipaaPackageSchema = {
        channel_type: 'fax',
      };
      if (faxCoverTo.trim()) payload.cover_page_to = faxCoverTo.trim();
      if (faxCoverFrom.trim()) payload.cover_page_from = faxCoverFrom.trim();
      if (faxCoverMessage.trim())
        payload.cover_page_message = faxCoverMessage.trim();

      await submitHipaaPackage({
        matterId,
        providerId: provider.id,
        requestId: request.id,
        submitHipaaPackageSchema: payload,
      }).unwrap();

      // Mark HIPAA package as successfully submitted
      setHipaaPackageSubmitted(true);
      toast.info('Fax transmission initiated. This may take a few minutes...');
      setHasFaxBeenInitiated(true);
      setShowFaxSubmitModal(false);
      startPolling();
    } catch (error) {
      toast.error('Failed to submit fax. Please try again.');
    } finally {
      setIsActionInProgress(false);
    }
  };

  // Confirm submission from the Mail modal
  const handleConfirmMailSubmit = async () => {
    setIsActionInProgress(true);
    try {
      setMailError(null);
      let payload: SubmitHipaaPackageSchema = {
        include_cheque: false,
        channel_type: 'mail',
        mailing_class: mailingClass,
        include_return_postage: includeReturnPostage,
      };
      if (includeCheque) {
        const amountStr = chequeAmount.trim();
        const amountRegex = /^\d+(\.\d{1,2})?$/;
        const parsed = Number(amountStr);
        if (!amountRegex.test(amountStr)) {
          setMailError(
            'Enter a valid amount with up to 2 decimals (e.g., 25.00).',
          );
          setIsActionInProgress(false);
          return;
        }
        if (!(parsed > 0)) {
          setMailError('Amount must be greater than $0.00.');
          setIsActionInProgress(false);
          return;
        }
        if (parsed > MAX_CHEQUE_AMOUNT) {
          setMailError(
            `Amount exceeds the ${MAX_CHEQUE_AMOUNT.toFixed(2)} limit.`,
          );
          setIsActionInProgress(false);
          return;
        }
        payload = {
          ...payload,
          include_cheque: true,
          cheque_amount: parsed.toFixed(2),
          ...(chequeMemo.trim() ? { cheque_memo: chequeMemo.trim() } : {}),
        };
      }

      await submitHipaaPackage({
        matterId,
        providerId: provider.id,
        requestId: request.id,
        submitHipaaPackageSchema: payload,
      }).unwrap();

      setHipaaPackageSubmitted(true);
      toast.info('Mail queued/sent to PostGrid. Processing…');
      setShowMailSubmitModal(false);
      // Start a wait window to keep the card visible for blockers/Finchy updates
      const until = Date.now() + MAIL_SUBMIT_WAIT_MS;
      sessionStorage.setItem(waitKey, String(until));
      setMailWaitUntil(until);
      // Clear any existing timer before scheduling a new one
      if (autoAdvanceTimer) {
        clearTimeout(autoAdvanceTimer);
        setAutoAdvanceTimer(null);
      }
      const newTimerId = scheduleAutoAdvance(until);
      if (newTimerId !== null) {
        setAutoAdvanceTimer(newTimerId);
      }
    } catch (error: unknown) {
      const data = (error as any)?.data;
      const detail: string | undefined =
        data?.detail || data?.error || data?.message;
      setMailError(
        detail || 'Failed to submit mail. Please review cheque details.',
      );
      toast.error('Failed to submit mail');
    } finally {
      setIsActionInProgress(false);
    }
  };

  const handleConfirmEmailSubmit = async () => {
    const trimmedSubject = emailSubject.trim();
    const trimmedBody = emailBody.trim();
    if (!trimmedSubject || !trimmedBody) {
      setEmailValidationError('Subject and body are required.');
      return;
    }

    if (emailSenderOption === 'records' && !RECORDS_FRONT_CHANNEL_ID.trim()) {
      toast.error('Records sender channel is not configured');
      return;
    }

    if (emailSenderOption === 'firm' && !firmFrontChannel?.address) {
      toast.error('Firm Front channel is not configured');
      return;
    }

    setEmailValidationError(null);
    setIsActionInProgress(true);
    let emailSubmitted = false;
    try {
      const payload: SubmitHipaaPackageSchemaWithFrontChannelOverride = {
        channel_type: 'email',
        email_subject: trimmedSubject,
        email_body: trimmedBody,
      };
      if (emailSenderOption === 'records') {
        payload.front_channel_id = RECORDS_FRONT_CHANNEL_ID.trim();
      }

      await submitHipaaPackage({
        matterId,
        providerId: provider.id,
        requestId: request.id,
        submitHipaaPackageSchema: payload,
      }).unwrap();
      emailSubmitted = true;
      toast.success('Email sent successfully!');
      setShowEmailSubmitModal(false);
      setEmailSubject('');
      setEmailBody('');
      await changeStatus(nextStatus, undefined, hasBlockers);
    } catch (error) {
      if (emailSubmitted) {
        toast.error('Email sent, but failed to update status.');
      } else {
        toast.error('Failed to send email. Please try again.');
      }
    } finally {
      setIsActionInProgress(false);
    }
  };

  // Show different action text based on fax status
  const getActionText = () => {
    if (isFaxChannel) {
      if (faxFailed) {
        return 'Retry Fax';
      }
      if (isPolling || faxStatus?.status === 'pending') {
        return 'Sending Fax...';
      }
    }
    if (isMailChannel && isInMailWaitWindow) {
      return 'Processing...';
    }
    if (isEmailChannel && isActionInProgress) {
      return 'Sending Email...';
    }
    return actionText;
  };

  // Get the appropriate handler based on fax status
  const getActionHandler = () => {
    if (isFaxChannel && faxFailed) {
      return handleRetryFax;
    }
    return handleAction;
  };

  return (
    <PizzaTrackerStep
      state={state}
      title={title}
      provider={provider}
      request={request}
      status={status}
    >
      <TrackerContent
        request={request}
        provider={provider}
        state={state}
        actionText={getActionText()}
        handleAction={getActionHandler()}
        isActionInProgress={isActionInProgress}
        faxFailed={faxFailed}
        handleManualOverride={handleManualOverride}
        isActionDisabled={
          isFaxChannel
            ? isFaxMissing || isHipaaPackageMissing
            : isMailChannel
              ? isAddressMissing || isHipaaPackageMissing || isInMailWaitWindow
              : isEmailChannel
                ? isEmailMissing ||
                  isFrontChannelMissing ||
                  isHipaaPackageMissing
                : false
        }
        disabledReason={
          isFaxChannel && (isFaxMissing || isHipaaPackageMissing)
            ? [
                isFaxMissing ? 'Add fax number' : null,
                isHipaaPackageMissing ? 'Complete HIPAA package' : null,
              ]
                .filter(Boolean)
                .join(' and ')
            : isMailChannel && (isAddressMissing || isHipaaPackageMissing)
              ? [
                  isAddressMissing ? 'Add mailing address' : null,
                  isHipaaPackageMissing ? 'Complete HIPAA package' : null,
                  isInMailWaitWindow ? 'Processing submission' : null,
                ]
                  .filter(Boolean)
                  .join(' and ')
              : isEmailChannel &&
                  (isEmailMissing ||
                    isFrontChannelMissing ||
                    isHipaaPackageMissing)
                ? [
                    isEmailMissing ? 'Add email address' : null,
                    isFrontChannelMissing
                      ? 'Configure email sender channel'
                      : null,
                    isHipaaPackageMissing ? 'Complete HIPAA package' : null,
                  ]
                    .filter(Boolean)
                    .join(' and ')
                : null
        }
      />

      {/* Mail submit modal */}
      {isMailChannel && showMailSubmitModal && (
        <Dialog
          open={showMailSubmitModal}
          onClose={() => setShowMailSubmitModal(false)}
          size="lg"
          zIndex={Z_INDEX_MODAL_NESTED}
          nested
        >
          <DialogBody>
            <DialogTitle>Submit HIPAA Package by Mail</DialogTitle>
            <DialogDescription>
              Configure optional cheque and confirm your mail submission.
            </DialogDescription>
            <div className="mt-4 space-y-4">
              <div className="flex flex-col gap-2">
                <div className="text-sm text-gray-700">Mailing class</div>
                <Select value={mailingClass} onValueChange={setMailingClass}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select mailing class" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    <SelectItem value="usps_first_class">
                      USPS First Class (default)
                    </SelectItem>
                    <SelectItem
                      value="usps_first_class_certified"
                      disabled
                      className="opacity-50"
                    >
                      USPS First Class — Certified (temporarily unavailable)
                    </SelectItem>
                    <SelectItem
                      value="usps_first_class_certified_return_receipt"
                      disabled
                      className="opacity-50"
                    >
                      USPS First Class — Certified + Return Receipt (temporarily
                      unavailable)
                    </SelectItem>
                    <SelectItem value="usps_express_2_day">
                      USPS Express 2 Day
                    </SelectItem>
                    <SelectItem value="usps_express_3_day">
                      USPS Express 3 Day
                    </SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-xs text-gray-500">
                  Defaults to First Class if omitted or invalid.
                </div>
              </div>

              <label
                htmlFor={returnPostageId}
                className="flex items-center gap-2 text-sm text-gray-700"
              >
                <input
                  id={returnPostageId}
                  type="checkbox"
                  checked={includeReturnPostage}
                  onChange={(e) => setIncludeReturnPostage(e.target.checked)}
                />
                Include return postage
              </label>

              <label
                htmlFor={includeChequeId}
                className="flex items-center gap-2 text-sm text-gray-700"
              >
                <input
                  type="checkbox"
                  id={includeChequeId}
                  checked={includeCheque}
                  onChange={(e) => setIncludeCheque(e.target.checked)}
                />
                Include cheque (optional)
              </label>
            </div>
            {includeCheque && (
              <div className="mt-4 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="Amount (e.g., 25.00)"
                      value={chequeAmount}
                      onChange={(e) => setChequeAmount(e.target.value)}
                    />
                  </div>
                  <div className="flex-[2]">
                    <Input
                      type="text"
                      placeholder="Memo (optional)"
                      value={chequeMemo}
                      onChange={(e) => setChequeMemo(e.target.value)}
                    />
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  Enter a positive amount up to ${MAX_CHEQUE_AMOUNT.toFixed(2)}.
                  Leave memo blank to use the default.
                </div>
                {mailError && (
                  <div className="text-xs text-red-600">{mailError}</div>
                )}
              </div>
            )}
            <DialogActions>
              <Button
                color="zinc"
                onClick={() => setShowMailSubmitModal(false)}
                disabled={isActionInProgress}
              >
                Cancel
              </Button>
              <Button
                color="indigo-steel"
                onClick={handleConfirmMailSubmit}
                disabled={isActionInProgress}
              >
                Confirm & Submit Mail
              </Button>
            </DialogActions>
          </DialogBody>
        </Dialog>
      )}

      {/* Email submit modal */}
      {isEmailChannel && showEmailSubmitModal && (
        <Dialog
          open={showEmailSubmitModal}
          onClose={() => {
            if (!isActionInProgress) {
              setShowEmailSubmitModal(false);
            }
          }}
          size="lg"
          zIndex={Z_INDEX_MODAL_NESTED}
          nested
        >
          <DialogBody>
            <DialogTitle>Send HIPAA Package by Email</DialogTitle>
            <DialogDescription>
              Review and customize the email before sending it to the provider.
            </DialogDescription>
            <div className="mt-4 space-y-4">
              {isEmailTemplatePending ? (
                <div className="flex items-center justify-center py-10 text-sm text-gray-600">
                  <div className="flex items-center gap-3">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                    <span>Loading email template…</span>
                  </div>
                </div>
              ) : (
                <>
                  {emailTemplateErrorMessage && (
                    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                      {emailTemplateErrorMessage}
                    </div>
                  )}
                  {/* To field */}
                  <div className="flex flex-col gap-2">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label
                      htmlFor={emailToId}
                      className="text-sm font-medium text-gray-900"
                    >
                      To
                    </label>
                    <Input
                      id={emailToId}
                      value={request.channel?.email || 'No email configured'}
                      disabled
                      className="bg-gray-50 text-gray-700"
                    />
                  </div>
                  {/* From field */}
                  <div className="flex flex-col gap-2">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="text-sm font-medium text-gray-900">
                      From
                    </label>
                    <Select
                      value={emailSenderOption}
                      onValueChange={(value) =>
                        setEmailSenderOption(value as EmailSenderOption)
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select sender" />
                      </SelectTrigger>
                      <SelectContent
                        className="bg-white border border-gray-200"
                        style={{ zIndex: Z_INDEX_DROPDOWN_IN_MODAL }}
                      >
                        <SelectItem
                          value="firm"
                          disabled={!firmFrontChannel?.address}
                          className={!firmFrontChannel?.address ? 'opacity-50' : ''}
                        >
                          {firmFrontChannel?.address
                            ? `Firm (${firmFrontChannel.address})`
                            : 'Firm Front channel (not configured)'}
                        </SelectItem>
                        <SelectItem
                          value="records"
                          disabled={!RECORDS_FRONT_CHANNEL_ID.trim()}
                          className={!RECORDS_FRONT_CHANNEL_ID.trim() ? 'opacity-50' : ''}
                        >
                          {RECORDS_SENDER_ADDRESS}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                      {emailSenderOption === 'firm' && !firmFrontChannel?.address && (
                        <div className="text-xs text-red-600">
                          Configure firm Front channel in Firms to send emails
                        </div>
                      )}
                      {emailSenderOption === 'records' &&
                        !RECORDS_FRONT_CHANNEL_ID.trim() && (
                          <div className="text-xs text-red-600">
                            Records sender channel ID not configured
                          </div>
                        )}
                      {isFirmChannelMissing && emailSenderOption === 'records' && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                          No firm-specific Front channel is configured. Emails will be sent from the shared records address. Consider configuring a firm-specific channel in Firm settings for a more personalized sender.
                        </div>
                      )}
                  </div>
                  <div className="flex flex-col gap-2">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label
                      htmlFor={emailSubjectId}
                      className="text-sm font-medium text-gray-900"
                    >
                      Subject
                    </label>
                    <Input
                      id={emailSubjectId}
                      value={emailSubject}
                      onChange={(e) => {
                        setEmailSubject(e.target.value);
                        if (emailValidationError) {
                          setEmailValidationError(null);
                        }
                      }}
                      placeholder="Email subject"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label
                      htmlFor={emailBodyId}
                      className="text-sm font-medium text-gray-900"
                    >
                      Body
                    </label>
                    <Textarea
                      id={emailBodyId}
                      value={emailBody}
                      onChange={(e) => {
                        setEmailBody(e.target.value);
                        if (emailValidationError) {
                          setEmailValidationError(null);
                        }
                      }}
                      rows={8}
                      placeholder="Email body"
                    />
                  </div>
                  {emailValidationError && (
                    <div className="text-xs text-red-600">
                      {emailValidationError}
                    </div>
                  )}
                </>
              )}
            </div>
            <DialogActions>
              <Button
                color="zinc"
                onClick={() => setShowEmailSubmitModal(false)}
                disabled={isActionInProgress}
              >
                Cancel
              </Button>
              <Button
                color="indigo-steel"
                onClick={handleConfirmEmailSubmit}
                disabled={isActionInProgress || isEmailTemplatePending}
              >
                Confirm & Send Email
              </Button>
            </DialogActions>
          </DialogBody>
        </Dialog>
      )}

      {/* Fax submit modal */}
      {isFaxChannel && showFaxSubmitModal && (
        <Dialog
          open={showFaxSubmitModal}
          onClose={() => setShowFaxSubmitModal(false)}
          size="lg"
          zIndex={Z_INDEX_MODAL_NESTED}
          nested
        >
          <DialogBody>
            <DialogTitle>Submit HIPAA Package by Fax</DialogTitle>
            <DialogDescription>
              Optionally add a fax cover page before submitting.
            </DialogDescription>
            <div className="mt-4 flex flex-col gap-3">
              <Input
                type="text"
                placeholder="Cover page To (optional)"
                value={faxCoverTo}
                onChange={(e) => setFaxCoverTo(e.target.value)}
              />
              <Input
                type="text"
                placeholder="Cover page From (optional)"
                value={faxCoverFrom}
                onChange={(e) => setFaxCoverFrom(e.target.value)}
              />
              <Input
                type="text"
                placeholder="Cover page Message (optional)"
                value={faxCoverMessage}
                onChange={(e) => setFaxCoverMessage(e.target.value)}
              />
            </div>
            <DialogActions>
              <Button
                color="zinc"
                onClick={() => setShowFaxSubmitModal(false)}
                disabled={isActionInProgress}
              >
                Cancel
              </Button>
              <Button
                color="indigo-steel"
                onClick={handleConfirmFaxSubmit}
                disabled={isActionInProgress}
              >
                Confirm & Submit Fax
              </Button>
            </DialogActions>
          </DialogBody>
        </Dialog>
      )}
    </PizzaTrackerStep>
  );
};
